import { Button, Stack, Text, Title } from '@mantine/core';
import React, { Component, type ErrorInfo, type ReactNode } from 'react';

import { dispatchChunkError } from 'components/NewVersionBar/NewVersionBar';

const CHUNK_LOAD_ERR =
  /Loading chunk \d+ failed|ChunkLoadError|Loading CSS chunk|\(missing:|Failed to fetch dynamically imported module|error loading dynamically imported module/i;

function isChunkLoadError(error: Error): boolean {
  return CHUNK_LOAD_ERR.test(error.message);
}

type Props = { children: ReactNode };
type State = { hasError: boolean; error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught:', error, errorInfo);
    if (typeof window !== 'undefined' && isChunkLoadError(error)) {
      dispatchChunkError();
    }
  }

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children } = this.props;
    if (hasError && error) {
      return (
        <Stack align="center" justify="center" p="xl" maw={480} mx="auto">
          <Title order={3}>Something went wrong</Title>
          <Text size="sm" color="dimmed">
            {error.message}
          </Text>
          <Button
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.href = '/dashboard';
            }}
          >
            Go to dashboard
          </Button>
        </Stack>
      );
    }
    return children;
  }
}
